"use client"

import { forwardRef, useImperativeHandle, useRef } from 'react'

interface WellData {
  api14: string
  operator_name: string
  field_name: string
  well_number: string
  lease_name: string
}

interface CasingString {
  string: string
  top_ft: number | null
  size_in: number
  bottom_ft: number
  hole_size_in: number
  cement_top_ft?: number | null
}

interface FormationTop {
  top_ft: number
  base_ft?: number | null
  formation: string
}

interface Perforation {
  top_ft: number
  bottom_ft: number
}

interface Tubing {
  top_ft: number | null
  size_in: number
  bottom_ft: number
}

interface ExistingTool {
  source?: string
  tool_type?: string
  type?: string  // New format uses "type" instead of "tool_type"
  material_description?: string
  top_ft?: number
  bottom_ft?: number
  depth_ft?: number  // New format uses "depth_ft" instead of "top_ft"
  open_hole?: boolean
  notes?: string | null
  description?: string  // New format uses "description"
}

interface Step {
  step_id?: number
  type: string
  formation?: string | null
  plug_type?: string | null
  top_ft: number | null
  bottom_ft: number | null
  sacks?: number | null
  display_name?: string
  regulatory_basis?: string[] | null
  plug_purpose?: string | null
  recipe?: {
    class?: string
  }
  tag_required?: boolean
  details?: {
    segments_calc?: Array<{
      inner_in?: number
      outer_in?: number
    }>
    annuli?: Array<{
      inner?: string
      outer?: string
      inner_od_in?: number
      outer_id_in?: number
      base_volume_bbl?: number
      interval_length_ft?: number
      capacity_bbl_per_ft?: number
    }>
    geometry_used?: {
      annulus?: string
      casing_id_in?: number
      stinger_od_in?: number
    }
    cement_class?: string
    wait_hours?: number
  }
}

interface Liner {
  size_in?: number
  top_ft?: number
  bottom_ft?: number
}

interface WellGeometry {
  casing_strings: CasingString[]
  formation_tops: FormationTop[]
  production_perforations: Perforation[]
  tubing: Tubing[]
  existing_tools?: ExistingTool[]
  mechanical_equipment?: ExistingTool[]  // Also support mechanical_equipment field
  liner?: Liner[]
  kop_ft?: number
  historic_cement_jobs?: Array<{
    job_type?: string
    interval_top_ft?: number
    interval_bottom_ft?: number
    cement_top_ft?: number
    sacks?: number
    slurry_density_ppg?: number | null
    additives?: any[]
    yield_ft3_per_sk?: number | null
  }>
}

interface ProposedPlugDiagramData {
  well: WellData
  well_geometry: WellGeometry
  payload: {
    steps: Step[]
    formations_targeted?: string[]
    materials_totals?: {
      total_sacks?: number
      total_bbl?: number
    }
  }
}

interface VisibilitySettings {
  casing: boolean
  tubing: boolean
  perforations: boolean
  formationTops: boolean
  newCementPlugs: boolean
  existingCement: boolean
  bridgePlugs: boolean
  existingTools: boolean
}

interface ProposedPlugDiagramProps {
  data: ProposedPlugDiagramData
  width?: number
  height?: number
  visibility?: VisibilitySettings
}

export interface ProposedPlugWBDHandle {
  getSvgElement: () => SVGSVGElement | null
}

const ProposedPlugDiagram = forwardRef<ProposedPlugWBDHandle, ProposedPlugDiagramProps>(function ProposedPlugDiagram({
  data,
  width = 700,
  height = 1000,
  visibility = {
    casing: true,
    tubing: true,
    perforations: true,
    formationTops: true,
    newCementPlugs: true,
    existingCement: true,
    bridgePlugs: true,
    existingTools: true,
  }
}, ref) {
  const svgRef = useRef<SVGSVGElement>(null)

  useImperativeHandle(ref, () => ({
    getSvgElement: () => svgRef.current,
  }))

  const svgWidth = width
  const svgHeight = height
  const wellCenterX = 180


  // Calculate max depth from all available data
  const depths = [
    ...data.well_geometry.casing_strings.map((c) => c.bottom_ft),
    ...(data.payload?.steps?.filter((s) => s.bottom_ft).map((s) => s.bottom_ft!) || []),
    ...(data.well_geometry.production_perforations?.map((p) => p.bottom_ft) || []),
    ...(data.well_geometry.tubing?.map((t) => t.bottom_ft) || []),
    ...(data.well_geometry.formation_tops?.map((f) => f.top_ft) || []),
    ...(data.well_geometry.kop_ft != null ? [data.well_geometry.kop_ft] : []),
  ]
  const maxDepth = Math.max(...depths)

  // Shallowest CIBP depth — no new cement plug should be proposed below this depth
  const cibpStepDepths = (data.payload?.steps || [])
    .filter(s => (s.type === 'bridge_plug' || s.type === 'mechanical_plug') && s.top_ft != null)
    .map(s => s.top_ft!)
  const shallowedCibpDepth = cibpStepDepths.length > 0 ? Math.min(...cibpStepDepths) : null

  const depthScale = (svgHeight - 100) / maxDepth
  const depthToY = (depth: number) => 50 + depth * depthScale
  const inchesToPixels = (inches: number) => inches * 3.2

  // Sort casing strings by hole size (largest to smallest)
  const sortedCasing = [...data.well_geometry.casing_strings].sort((a, b) => b.hole_size_in - a.hole_size_in)

  const formationColors: Record<string, { bg: string; border: string; text: string }> = {
    WOLFCAMP: { bg: "#FEF3C7", border: "#D97706", text: "#92400E" },
    STRAWN: { bg: "#DBEAFE", border: "#2563EB", text: "#1E40AF" },
    DEVONIAN: { bg: "#FEE2E2", border: "#DC2626", text: "#991B1B" },
    CLEARFORK: { bg: "#D1FAE5", border: "#059669", text: "#065F46" },
    SPRABERRY: { bg: "#E0E7FF", border: "#4F46E5", text: "#3730A3" },
    DEAN: { bg: "#FED7AA", border: "#EA580C", text: "#9A3412" },
    "SAN ANDRES": { bg: "#FEF9C3", border: "#CA8A04", text: "#713F12" },
    "SANTA ROSA": { bg: "#FCE7F3", border: "#DB2777", text: "#9D174D" },
    YATES: { bg: "#E9D5FF", border: "#A855F7", text: "#6B21A8" },
    "SEVEN RIVERS": { bg: "#BAE6FD", border: "#0284C7", text: "#075985" },
    QUEEN: { bg: "#FED7E2", border: "#F43F5E", text: "#9F1239" },
    GRAYBURG: { bg: "#FEE2C5", border: "#F97316", text: "#9A3412" },
    DEFAULT: { bg: "#F3F4F6", border: "#6B7280", text: "#374151" },
  }

  const getFormationColors = (formation: string) => {
    const upperFormation = formation.toUpperCase()
    for (const key of Object.keys(formationColors)) {
      if (upperFormation.includes(key)) {
        return formationColors[key]
      }
    }
    return formationColors.DEFAULT
  }

  const totalSacks =
    data.payload?.materials_totals?.total_sacks ||
    data.payload?.steps?.reduce((sum, step) => sum + (step.sacks || 0), 0) ||
    0

  interface LabelItem {
    y: number
    text: string
    subtext?: string
    color: string
  }

  interface ForkedLabelItem extends LabelItem {
    topY: number
    bottomY: number
    isForked: true
  }

  interface AdjustedLabelItem extends LabelItem {
    adjustedY: number
    topY?: number
    bottomY?: number
    isForked?: boolean
  }

  const labels: LabelItem[] = []

  const getStepLabel = (step: Step): string => {
    if (step.display_name) return step.display_name
    const formation = step.formation
    // Formation-specific plug types use the formation name as the label prefix
    if (formation) {
      if (step.type === "formation_plug") return `${formation} Plug`
      if (step.type === "formation_top_plug") return `${formation} Top Plug`
    }
    const typeLabels: Record<string, string> = {
      cement_plug: "cement plug",
      bridge_plug_cap: "CIBP Cap",
      cibp_cap: "CIBP Cap",
      formation_plug: "formation plug",
      formation_top_plug: "formation top plug",
      surface_casing_shoe_plug: "surface shoe plug",
      uqw_isolation_plug: "UQW isolation plug",
      top_plug: "top plug",
      existing_cement: "Existing Cement",
      mechanical_plug: "New CIBP",
    }
    return typeLabels[step.type] || step.type.replace(/_/g, " ")
  }

  // Add step labels - include cement plugs (which have sacks), spot plugs, and existing cement
  data.payload?.steps
    ?.filter((step) => {
      if (step.top_ft == null) return false
      // Don't label plugs that are filtered out because they're below a CIBP
      if (step.type !== "existing_cement" && shallowedCibpDepth != null && step.top_ft > shallowedCibpDepth) return false
      return step.sacks || step.plug_type === "spot_plug" || step.plug_type === "perf_and_squeeze_plug" || step.type === "bridge_plug_cap" || step.type === "cibp_cap" || step.type === "existing_cement"
    })
    .forEach((step) => {
      const midDepth = step.bottom_ft ? (step.top_ft! + step.bottom_ft) / 2 : step.top_ft!
      const depthRange = step.bottom_ft
        ? `${step.top_ft!.toLocaleString()} - ${step.bottom_ft.toLocaleString()} ft`
        : `${step.top_ft!.toLocaleString()} ft`
      const isExistingCement = step.type === "existing_cement"
      const cementClass = step.recipe?.class || step.details?.cement_class || ""
      const isPerfSqueeze = step.type === "perforate_and_squeeze_plug" || step.plug_type === "perf_and_squeeze_plug"
      const wocTag = isPerfSqueeze && step.details?.wait_hours
        ? `WOC ${step.details.wait_hours}hrs & Tag`
        : ""
      const classStr = cementClass ? ` Class ${cementClass}` : ""
      labels.push({
        y: depthToY(midDepth),
        text: getStepLabel(step),
        subtext: `${depthRange}${step.sacks ? ` ${step.sacks} sks${classStr}` : ""}${wocTag ? `\n${wocTag}` : ""}`,
        color: isExistingCement ? "#6B7280" : "#166534",
      })
    })

  // Add bridge plug labels
  data.payload?.steps
    ?.filter((step) => step.type === "bridge_plug" || step.type === "mechanical_plug")
    .forEach((step) => {
      const depthText = step.top_ft ? `${step.top_ft.toLocaleString()} ft` : ""
      labels.push({
        y: depthToY(step.top_ft!),
        text: "CIBP",
        subtext: `${depthText}\n${step.sacks ? `${step.sacks} sks` : "Mechanical"}`,
        color: "#92400E",
      })
    })

  // Add perforation labels (with special forked connector marker)
  const perfLabels: ForkedLabelItem[] = []
  data.well_geometry.production_perforations?.forEach((perf) => {
    const topFt = perf.top_ft ?? 0
    const bottomFt = perf.bottom_ft ?? 0
    const midDepth = (topFt + bottomFt) / 2
    perfLabels.push({
      y: depthToY(midDepth),
      topY: depthToY(topFt),
      bottomY: depthToY(bottomFt),
      text: `Perf: ${topFt.toLocaleString()}-${bottomFt.toLocaleString()} ft`,
      color: "#DC2626",
      isForked: true,
    })
  })

  // Add existing tools labels (CIBP, Packers, Retainers, etc.)
  // Merge both existing_tools and mechanical_equipment arrays
  const allExistingTools = [
    ...(data.well_geometry.existing_tools || []),
    ...(data.well_geometry.mechanical_equipment || [])
  ]
  
  // Remove duplicates based on type and depth
  const uniqueTools = allExistingTools.filter((tool, index, self) => {
    const toolDepth = tool.depth_ft ?? tool.top_ft
    const toolType = tool.type || tool.tool_type
    return index === self.findIndex((t) => 
      (t.depth_ft ?? t.top_ft) === toolDepth && (t.type || t.tool_type) === toolType
    )
  })
  
  uniqueTools.forEach((tool) => {
    const toolType = tool.type || tool.tool_type || "Unknown Tool"
    const toolDepth = tool.depth_ft ?? tool.top_ft
    
    if (toolDepth == null) return // Skip if no depth available
    
    // Determine label and color based on tool type
    let labelText = "Existing Tool"
    let labelColor = "#92400E"
    
    const toolTypeUpper = toolType.toUpperCase()
    if (toolTypeUpper.includes("CIBP") || toolTypeUpper.includes("BRIDGE")) {
      labelText = "Existing CIBP"
      labelColor = "#92400E"
    } else if (toolTypeUpper.includes("PACKER")) {
      labelText = "Existing Packer"
      labelColor = "#7C2D12"
    } else if (toolTypeUpper.includes("RETAINER")) {
      labelText = "Existing Retainer"
      labelColor = "#854D0E"
    } else {
      labelText = `Existing ${toolType}`
      labelColor = "#78350F"
    }
    
    labels.push({
      y: depthToY(toolDepth),
      text: labelText,
      subtext: `${toolDepth.toLocaleString()} ft${tool.description ? `\n${tool.description.substring(0, 30)}` : ""}`,
      color: labelColor,
    })
  })

  // Merge regular labels and perforation labels
  const allLabels = [...labels, ...perfLabels]
  allLabels.sort((a, b) => a.y - b.y)

  const minLabelGap = 35 // Minimum pixels between labels
  
  // Build adjusted labels array using forEach to avoid circular reference issues
  const adjustedLabels: AdjustedLabelItem[] = []
  allLabels.forEach((label, idx) => {
    if (idx === 0) {
      adjustedLabels.push({ ...label, adjustedY: label.y })
      return
    }

    const prevAdjusted = adjustedLabels[idx - 1]
    const prevAdjustedY = prevAdjusted.adjustedY

    if (label.y - prevAdjustedY < minLabelGap) {
      const newY = prevAdjustedY + minLabelGap
      adjustedLabels.push({ ...label, adjustedY: newY })
    } else {
      adjustedLabels.push({ ...label, adjustedY: label.y })
    }
  })

  const labelStartX = wellCenterX + 100

  return (
    <div className="w-full h-full bg-gray-50 p-8 overflow-auto">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="mb-6 pb-4 border-b-2 border-brand-700">
          <h1 className="text-2xl font-bold text-brand-700 mb-2">Proposed Plug Wellbore Diagram</h1>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Well:</span>{" "}
              <span className="font-semibold">
                {data.well.lease_name} {data.well.well_number}
              </span>
            </div>
            <div>
              <span className="text-gray-600">API:</span> <span className="font-semibold">{data.well.api14}</span>
            </div>
            <div>
              <span className="text-gray-600">Operator:</span>{" "}
              <span className="font-semibold">{data.well.operator_name}</span>
            </div>
            <div>
              <span className="text-gray-600">Field:</span>{" "}
              <span className="font-semibold">{data.well.field_name}</span>
            </div>
            {totalSacks > 0 && (
              <div>
                <span className="text-gray-600">Total Cement:</span>{" "}
                <span className="font-semibold">{totalSacks.toLocaleString()} sacks</span>
              </div>
            )}
            {data.payload?.formations_targeted && data.payload.formations_targeted.length > 0 && (
              <div>
                <span className="text-gray-600">Targeted Formations:</span>{" "}
                <span className="font-semibold">{data.payload.formations_targeted.join(", ")}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-center">
          <svg ref={svgRef} width={svgWidth} height={svgHeight}>
            <defs>
              {/* New cement pattern - green with black dots */}
              <pattern id="cementPattern" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                <rect width="10" height="10" fill="#22c55e" />
                <circle cx="3" cy="3" r="1.5" fill="#000" />
                <circle cx="8" cy="7" r="1" fill="#000" />
                <circle cx="5" cy="9" r="0.8" fill="#000" />
              </pattern>

              {/* Existing cement pattern - grey with black dots */}
              <pattern id="existingCementPattern" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                <rect width="10" height="10" fill="#9CA3AF" />
                <circle cx="3" cy="3" r="1.5" fill="#000" />
                <circle cx="8" cy="7" r="1" fill="#000" />
                <circle cx="5" cy="9" r="0.8" fill="#000" />
              </pattern>

              {/* Perforation pattern - red diagonal stripes */}
              <pattern
                id="perfPattern"
                x="0"
                y="0"
                width="8"
                height="8"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect width="4" height="8" fill="#EF4444" />
                <rect x="4" width="4" height="8" fill="#DC2626" />
              </pattern>
            </defs>

            {/* Ground line */}
            <line
              x1={wellCenterX - 80}
              y1={depthToY(0)}
              x2={wellCenterX + 80}
              y2={depthToY(0)}
              stroke="#8B4513"
              strokeWidth="4"
            />
            {/* Surface label on far left to match depth markers */}
            <text
              x={5}
              y={depthToY(0) + 5}
              fontSize="11"
              fill="#666"
              fontWeight="bold"
              fontFamily="monospace"
            >
              0' Surface
            </text>

            {/* Wellhead symbol at surface — T-shaped cap spanning outermost casing */}
            {(() => {
              const widestCasing = sortedCasing[0]
              if (!widestCasing) return null
              const holeRadius = inchesToPixels(widestCasing.hole_size_in || widestCasing.size_in * 1.3) / 2
              const wellheadWidth = holeRadius + 15
              const groundY = depthToY(0)
              return (
                <g>
                  {/* Horizontal cap bar */}
                  <rect
                    x={wellCenterX - wellheadWidth}
                    y={groundY - 6}
                    width={wellheadWidth * 2}
                    height={4}
                    fill="#374151"
                    rx="1"
                  />
                  {/* Left stub */}
                  <rect
                    x={wellCenterX - wellheadWidth}
                    y={groundY - 12}
                    width={4}
                    height={10}
                    fill="#374151"
                  />
                  {/* Right stub */}
                  <rect
                    x={wellCenterX + wellheadWidth - 4}
                    y={groundY - 12}
                    width={4}
                    height={10}
                    fill="#374151"
                  />
                </g>
              )
            })()}

            {/* Depth markers every 250 feet - positioned on far left to avoid formation label overlap */}
            {Array.from({ length: Math.floor(maxDepth / 250) + 1 }).map((_, i) => {
              const depth = i * 250
              if (depth === 0) return null // Skip 0, already labeled as surface
              if (depth > maxDepth) return null
              
              const y = depthToY(depth)
              return (
                <g key={`depth-marker-${depth}`}>
                  {/* Small tick mark on far left */}
                  <line
                    x1={0}
                    y1={y}
                    x2={10}
                    y2={y}
                    stroke="#999"
                    strokeWidth="1"
                  />
                  {/* Depth label - subtle gray, smaller font */}
                  <text
                    x={12}
                    y={y + 3}
                    fontSize="9" 
                    fill="#999"
                    fontFamily="monospace"
                  >
                    {depth.toLocaleString()}'
                  </text>
                </g>
              )
            })}

            {/* Draw hole sections (dotted lines) */}
            {sortedCasing.map((casing, idx) => {
              const startDepth = idx === 0 ? 0 : sortedCasing[idx - 1].bottom_ft
              const holeWidth = inchesToPixels(casing.hole_size_in)

              return (
                <g key={`hole-${idx}`}>
                  <line
                    x1={wellCenterX - holeWidth / 2}
                    y1={depthToY(startDepth)}
                    x2={wellCenterX - holeWidth / 2}
                    y2={depthToY(casing.bottom_ft)}
                    stroke="#999"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                  <line
                    x1={wellCenterX + holeWidth / 2}
                    y1={depthToY(startDepth)}
                    x2={wellCenterX + holeWidth / 2}
                    y2={depthToY(casing.bottom_ft)}
                    stroke="#999"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </g>
              )
            })}


            {/* Draw existing cement in casing annulus */}
            {visibility.existingCement && sortedCasing.map((casing, idx) => {
              // Skip if no cement data — null means unknown, not surface-to-shoe
              if (casing.cement_top_ft == null) return null
              const cementTopFt = casing.cement_top_ft
              const cementBottomFt = casing.bottom_ft

              // Only draw if cement top is less than bottom (valid range)
              if (cementTopFt >= cementBottomFt) {
                return null
              }

              const topY = depthToY(cementTopFt)
              const bottomY = depthToY(cementBottomFt)
              const height = Math.max(10, bottomY - topY)

              // Only draw cement for the outermost casing (against open hole), not between casing strings
              const outerCasing = idx > 0 ? sortedCasing[idx - 1] : null
              if (outerCasing) return null

              const innerRadius = inchesToPixels(casing.size_in) / 2
              const outerRadius = inchesToPixels(casing.hole_size_in) / 2

              if (!casing.hole_size_in || outerRadius <= innerRadius) {
                return null
              }

              return (
                <g key={`existing-cement-${idx}`}>
                  {/* Outer ring of annulus */}
                  <rect
                    x={wellCenterX - outerRadius}
                    y={topY}
                    width={outerRadius * 2}
                    height={height}
                    fill="url(#existingCementPattern)"
                    opacity="0.8"
                  />
                  {/* Inner cutout (white to show the casing inside) */}
                  <rect
                    x={wellCenterX - innerRadius}
                    y={topY}
                    width={innerRadius * 2}
                    height={height}
                    fill="white"
                  />
                </g>
              )
            })}

            {/* Draw cement plugs - no inline labels */}
            {visibility.newCementPlugs && data.payload?.steps
              ?.filter((step) => {
                if (!step.top_ft || !step.bottom_ft) return false
                if (step.type === "existing_cement") return false  // Rendered separately in gray
                // Never render a new plug below the shallowest CIBP in the plan
                if (shallowedCibpDepth != null && step.top_ft > shallowedCibpDepth) return false
                return step.sacks || step.plug_type === "spot_plug" || step.plug_type === "perf_and_squeeze_plug" || step.type === "bridge_plug_cap" || step.type === "cibp_cap"
              })
              .map((step, stepIdx) => {
                // Handle potentially inverted intervals (ensure we draw from shallow to deep)
                const shallowerDepth = Math.min(step.top_ft!, step.bottom_ft!)
                const deeperDepth = Math.max(step.top_ft!, step.bottom_ft!)
                
                const topY = depthToY(shallowerDepth)
                const bottomY = depthToY(deeperDepth)
                const height = Math.abs(bottomY - topY) || 10

                // Find which casing this plug is in — use INNERMOST casing at this depth
                // sortedCasing is outermost-first (index 0 = largest), so search from the end
                let casingIdx = sortedCasing.length - 1
                let currentCasing = sortedCasing[sortedCasing.length - 1]
                for (let i = sortedCasing.length - 1; i >= 0; i--) {
                  if (shallowerDepth <= sortedCasing[i].bottom_ft) {
                    casingIdx = i
                    currentCasing = sortedCasing[i]
                    break
                  }
                }

                if (!currentCasing) return null

                // Calculate interior cement width (inside the casing ID, not OD)
                // Casing ID is typically ~87-92% of OD depending on weight
                const casingID = currentCasing.size_in * 0.87  // Approximate ID
                const interiorCementWidth = inchesToPixels(casingID)

                // Check if this is a perf & squeeze plug
                const isPerfsqueeze = step.plug_type === "perf_and_squeeze_plug"
                
                // Get cement dimensions from details (check annuli array first, then segments_calc)
                let innerDiameter = step.details?.segments_calc?.[2]?.inner_in
                let outerDiameter = step.details?.segments_calc?.[2]?.outer_in
                
                // Prioritize annuli data if available (production-to-openhole squeeze)
                if (step.details?.annuli && step.details.annuli.length > 0) {
                  const annulus = step.details.annuli[0] // Use first annulus
                  innerDiameter = annulus.inner_od_in  // Inner is the casing OD
                  outerDiameter = annulus.outer_id_in  // Outer is the hole ID or outer casing ID
                }
                
                const geometryInner = step.details?.geometry_used?.casing_id_in
                const geometryOuter = step.details?.geometry_used?.stinger_od_in

                return (
                  <g key={`plug-${step.step_id ?? stepIdx}`}>
                    {/* For perf & squeeze: show squeezed cement in annulus FIRST (draw below interior) */}
                    {isPerfsqueeze && casingIdx < sortedCasing.length && (
                      (() => {
                        let innerRadius = 0  // Casing OD (where annular cement starts)
                        let outerRadius = 0  // Hole size or outer casing ID (where annular cement ends)
                        
                        // Case 1: Has annuli with inner_od_in/outer_id_in
                        if (innerDiameter && outerDiameter) {
                          innerRadius = inchesToPixels(innerDiameter) / 2  // Casing OD
                          outerRadius = inchesToPixels(outerDiameter) / 2  // Hole ID or outer casing ID
                        }
                        // Case 2: Has geometry_used (perf in open hole)
                        else if (geometryInner && geometryOuter) {
                          innerRadius = inchesToPixels(geometryInner) / 2
                          outerRadius = inchesToPixels(geometryOuter) / 2
                        }
                        
                        if (innerRadius === 0 || outerRadius === 0) {
                          return null
                        }
                        
                        // Get the maximum outer radius (either next casing or hole size)
                        const currentCasing = sortedCasing[casingIdx]
                        const outerCasing = casingIdx > 0 ? sortedCasing[casingIdx - 1] : null
                        if (!currentCasing) return null

                        let annulusOuterRadius = outerRadius
                        
                        // If cement goes beyond outerRadius, extend to next outer casing or hole size
                        if (outerCasing) {
                          // There's another casing outside - cement goes to its ID
                          const outerCasingRadius = inchesToPixels(outerCasing.size_in) / 2  // Cement fills to casing wall
                          annulusOuterRadius = Math.max(outerRadius, outerCasingRadius)
                        } else if (currentCasing && currentCasing.hole_size_in) {
                          // No outer casing - cement goes to open hole
                          const holeRadius = inchesToPixels(currentCasing.hole_size_in) / 2
                          annulusOuterRadius = Math.max(outerRadius, holeRadius)
                        }
                        
                        // Calculate the white cutout radius - should be the INSIDE of the casing (casing ID)
                        // This preserves the interior cement inside the casing
                        // Casing ID is typically ~87% of OD for standard wall pipe
                        const currentCasingID = currentCasing.size_in * 0.87  // Approximate ID from OD
                        const casingIDRadius = inchesToPixels(currentCasingID) / 2
                        
                        // Check if the specific annulus being filled is already cemented
                        // We need to determine which annulus this plug is filling
                        let skipAnnularCement = false
                        const historicCement = data.well_geometry?.historic_cement_jobs || []
                        
                        // Determine which annulus this plug is trying to fill
                        // Check which casing strings exist at this depth
                        const casingAtDepth = sortedCasing.filter(c => shallowerDepth <= c.bottom_ft)
                        
                        // For plugs within surface casing interval, check if production-to-surface is cemented
                        if (casingAtDepth.length >= 2) {
                          // There are 2+ casings at this depth (e.g., production inside surface)
                          // Check if the annulus between them is cemented
                          // innerCasing would be casingAtDepth[length - 1] (Production - innermost)
                          // outerCasing would be casingAtDepth[length - 2] (Surface - next outer)
                          
                          // Look for a cement job that cemented this annulus at this depth
                          for (const job of historicCement) {
                            const jobTop = job.cement_top_ft ?? 0
                            const jobBottom = job.interval_bottom_ft ?? 0
                            
                            // Check if cement job covers this depth range and goes to surface
                            const coversThisDepth = (
                              shallowerDepth >= jobTop && 
                              deeperDepth <= jobBottom
                            )
                            
                            // Surface cement job indicates production-to-surface annulus is cemented
                            const isSurfaceAnnulusCement = job.job_type === "surface" && jobTop === 0
                            
                            if (coversThisDepth && isSurfaceAnnulusCement) {
                              skipAnnularCement = true
                              break
                            }
                          }
                        }

                        // Also check casing cement_top_ft — if the annulus at this depth
                        // is already cemented (plug depth is between cement_top_ft and shoe),
                        // don't draw new annular cement
                        if (!skipAnnularCement) {
                          // Check the casing that this plug is inside
                          const plugCasing = sortedCasing[casingIdx]
                          if (plugCasing?.cement_top_ft != null) {
                            const cementTop = plugCasing.cement_top_ft
                            const cementBottom = plugCasing.bottom_ft
                            // If the entire plug interval is within the cemented zone, skip annular cement
                            if (shallowerDepth >= cementTop && deeperDepth <= cementBottom) {
                              skipAnnularCement = true
                            }
                          }
                          // Note: outer casing's cement_top_ft describes cement OUTSIDE that casing
                          // (e.g., 9⅝"-to-surface annulus), not the annulus being filled here
                          // (production-to-intermediate). Only the plug's own casing cement_top_ft matters.
                        }

                        // Only draw annular cement if there's actual annulus space and it's not already cemented
                        if (!skipAnnularCement && annulusOuterRadius > innerRadius) {
                          return (
                            <g key={`perf-squeeze-annulus-${step.step_id ?? stepIdx}`}>
                              {/* Cement ring in annulus/open hole */}
                              <rect
                                x={wellCenterX - annulusOuterRadius}
                                y={topY}
                                width={annulusOuterRadius * 2}
                                height={height}
                                fill="url(#cementPattern)"
                                opacity="0.8"
                              />
                              {/* Cut out the inner part - but only to the casing ID, not OD */}
                              {/* This preserves the interior cement inside the casing */}
                              <rect
                                x={wellCenterX - casingIDRadius}
                                y={topY}
                                width={casingIDRadius * 2}
                                height={height}
                                fill="white"
                              />
                            </g>
                          )
                        }
                        return null
                      })()
                    )}
                    
                    {/* Cement in interior (inside production casing/tubing) - draw LAST (on top) */}
                    {/* Use interior cement width (casing ID) not full casing width (OD) */}
                    <rect
                      x={wellCenterX - interiorCementWidth / 2}
                      y={topY}
                      width={interiorCementWidth}
                      height={height}
                      fill="url(#cementPattern)"
                      stroke="#22C55E"
                      strokeWidth="2"
                    />
                  </g>
                )
              })}

            {/* Draw bridge plugs (CIBP) - no inline labels */}
            {visibility.bridgePlugs && data.payload?.steps
              ?.filter((step) => step.type === "bridge_plug" || step.type === "mechanical_plug")
              .map((step, bridgeIdx) => {
                const y = depthToY(step.top_ft!)

                let casingWidth = inchesToPixels(sortedCasing[sortedCasing.length - 1]?.size_in || 7)
                for (const casing of sortedCasing) {
                  if (step.top_ft! <= casing.bottom_ft) {
                    casingWidth = inchesToPixels(casing.size_in)
                    break
                  }
                }

                const boxHeight = 20
                const boxWidth = casingWidth

                return (
                  <g key={`bridge-${step.step_id ?? bridgeIdx}`}>
                    <rect
                      x={wellCenterX - boxWidth / 2}
                      y={y - boxHeight}
                      width={boxWidth}
                      height={boxHeight}
                      fill="#FCD34D"
                      stroke="#000"
                      strokeWidth="2"
                    />
                    <line
                      x1={wellCenterX - boxWidth / 2 + 2}
                      y1={y - boxHeight + 2}
                      x2={wellCenterX + boxWidth / 2 - 2}
                      y2={y - 2}
                      stroke="#000"
                      strokeWidth="3"
                    />
                    <line
                      x1={wellCenterX + boxWidth / 2 - 2}
                      y1={y - boxHeight + 2}
                      x2={wellCenterX - boxWidth / 2 + 2}
                      y2={y - 2}
                      stroke="#000"
                      strokeWidth="3"
                    />
                  </g>
                )
              })}

            {/* Draw existing cement steps from payload - rendered with gray existingCementPattern */}
            {visibility.existingCement && data.payload?.steps
              ?.filter((step) => step.type === "existing_cement" && step.top_ft != null && step.bottom_ft != null)
              .map((step, stepIdx) => {
                const shallowerDepth = Math.min(step.top_ft!, step.bottom_ft!)
                const deeperDepth = Math.max(step.top_ft!, step.bottom_ft!)
                const topY = depthToY(shallowerDepth)
                const bottomY = depthToY(deeperDepth)
                const height = Math.abs(bottomY - topY) || 10

                // Find which casing this step is in
                let currentCasing = sortedCasing[sortedCasing.length - 1]
                for (let i = 0; i < sortedCasing.length; i++) {
                  if (shallowerDepth <= sortedCasing[i].bottom_ft) {
                    currentCasing = sortedCasing[i]
                    break
                  }
                }
                if (!currentCasing) return null

                // Use casing ID (approx) for interior width
                const casingID = currentCasing.size_in * 0.87
                const interiorWidth = inchesToPixels(casingID)

                return (
                  <g key={`existing-cement-step-${step.step_id ?? stepIdx}`}>
                    {/* Interior existing cement - amber fill with dashed border, distinct from green new cement */}
                    <rect
                      x={wellCenterX - interiorWidth / 2}
                      y={topY}
                      width={interiorWidth}
                      height={height}
                      fill="#D97706"
                      opacity="0.4"
                      stroke="#92400E"
                      strokeWidth="2"
                      strokeDasharray="6,3"
                    />
                  </g>
                )
              })}

            {/* Draw existing tools (CIBPs, Packers, Retainers, etc.) - no inline labels */}
            {visibility.existingTools && (() => {
              // Merge both existing_tools and mechanical_equipment arrays
              const allTools = [
                ...(data.well_geometry.existing_tools || []),
                ...(data.well_geometry.mechanical_equipment || [])
              ]
              
              // Remove duplicates based on type and depth
              const uniqueTools = allTools.filter((tool, index, self) => {
                const toolDepth = tool.depth_ft ?? tool.top_ft
                const toolType = tool.type || tool.tool_type
                return index === self.findIndex((t) => 
                  (t.depth_ft ?? t.top_ft) === toolDepth && (t.type || t.tool_type) === toolType
                )
              })
              
              return uniqueTools.map((tool, idx) => {
                const toolDepth = tool.depth_ft ?? tool.top_ft
                if (toolDepth == null) return null // Skip if no depth
                
                const y = depthToY(toolDepth)
                const toolType = (tool.type || tool.tool_type || "").toUpperCase()

                let casingWidth = inchesToPixels(sortedCasing[sortedCasing.length - 1]?.size_in || 7)
                for (const casing of sortedCasing) {
                  if (toolDepth <= casing.bottom_ft) {
                    casingWidth = inchesToPixels(casing.size_in)
                    break
                  }
                }

                const boxHeight = 20
                const boxWidth = casingWidth
                
                // Determine fill and stroke based on tool type
                let fillColor = "#FEF3C7"
                let strokeColor = "#92400E"
                let pattern = "3,3" // Default dashed
                
                if (toolType.includes("PACKER")) {
                  fillColor = "#FED7AA"
                  strokeColor = "#9A3412"
                  pattern = "5,2"
                } else if (toolType.includes("RETAINER")) {
                  fillColor = "#FEF9C3"
                  strokeColor = "#854D0E"
                  pattern = "4,2"
                }

                return (
                  <g key={`existing-tool-${idx}`}>
                    <rect
                      x={wellCenterX - boxWidth / 2}
                      y={y - boxHeight}
                      width={boxWidth}
                      height={boxHeight}
                      fill={fillColor}
                      stroke={strokeColor}
                      strokeWidth="2"
                      strokeDasharray={pattern}
                    />
                    {/* Draw X pattern for CIBP/Bridge plugs, horizontal lines for Packers/Retainers */}
                    {(toolType.includes("CIBP") || toolType.includes("BRIDGE")) ? (
                      <>
                        <line
                          x1={wellCenterX - boxWidth / 2 + 2}
                          y1={y - boxHeight + 2}
                          x2={wellCenterX + boxWidth / 2 - 2}
                          y2={y - 2}
                          stroke={strokeColor}
                          strokeWidth="2"
                        />
                        <line
                          x1={wellCenterX + boxWidth / 2 - 2}
                          y1={y - boxHeight + 2}
                          x2={wellCenterX - boxWidth / 2 + 2}
                          y2={y - 2}
                          stroke={strokeColor}
                          strokeWidth="2"
                        />
                      </>
                    ) : (
                      <>
                        {/* Horizontal lines for Packers/Retainers */}
                        <line
                          x1={wellCenterX - boxWidth / 2 + 2}
                          y1={y - 5}
                          x2={wellCenterX + boxWidth / 2 - 2}
                          y2={y - 5}
                          stroke={strokeColor}
                          strokeWidth="2"
                        />
                        <line
                          x1={wellCenterX - boxWidth / 2 + 2}
                          y1={y}
                          x2={wellCenterX + boxWidth / 2 - 2}
                          y2={y}
                          stroke={strokeColor}
                          strokeWidth="2"
                        />
                        <line
                          x1={wellCenterX - boxWidth / 2 + 2}
                          y1={y + 5}
                          x2={wellCenterX + boxWidth / 2 - 2}
                          y2={y + 5}
                          stroke={strokeColor}
                          strokeWidth="2"
                        />
                      </>
                    )}
                  </g>
                )
              })
            })()}

            {/* Draw tubing - no inline labels */}
            {visibility.tubing && data.well_geometry.tubing?.map((tub, idx) => {
              const tubingWidth = inchesToPixels(tub.size_in)
              return (
                <g key={`tubing-${idx}`}>
                  <line
                    x1={wellCenterX - tubingWidth / 2}
                    y1={depthToY(tub.top_ft || 0)}
                    x2={wellCenterX - tubingWidth / 2}
                    y2={depthToY(tub.bottom_ft)}
                    stroke="#F59E0B"
                    strokeWidth="2"
                    strokeDasharray="3,3"
                  />
                  <line
                    x1={wellCenterX + tubingWidth / 2}
                    y1={depthToY(tub.top_ft || 0)}
                    x2={wellCenterX + tubingWidth / 2}
                    y2={depthToY(tub.bottom_ft)}
                    stroke="#F59E0B"
                    strokeWidth="2"
                    strokeDasharray="3,3"
                  />
                </g>
              )
            })}

            {/* Draw liners with hanger symbol and liner top label */}
            {data.well_geometry.liner?.map((liner, idx) => {
              const linerWidth = inchesToPixels(liner.size_in || 4.5)
              const hangerY = depthToY(liner.top_ft || 0)
              const bottomY = depthToY(liner.bottom_ft || 0)
              const linerLeftX = wellCenterX - linerWidth / 2
              const linerRightX = wellCenterX + linerWidth / 2
              const labelX = linerLeftX - 8
              return (
                <g key={`liner-${idx}`}>
                  <line x1={linerLeftX} y1={hangerY} x2={linerLeftX} y2={bottomY} stroke="#666" strokeWidth="2" strokeDasharray="12,4" />
                  <line x1={linerRightX} y1={hangerY} x2={linerRightX} y2={bottomY} stroke="#666" strokeWidth="2" strokeDasharray="12,4" />
                  {/* Liner hanger symbol — horizontal stubs at top */}
                  <line x1={linerLeftX - 4} y1={hangerY} x2={linerLeftX + 2} y2={hangerY} stroke="#6B7280" strokeWidth="2" />
                  <line x1={linerRightX - 2} y1={hangerY} x2={linerRightX + 4} y2={hangerY} stroke="#6B7280" strokeWidth="2" />
                  {/* Liner top label */}
                  <text x={labelX} y={hangerY - 6} fontSize="10" fill="#6B7280" fontWeight="600" textAnchor="end">
                    Liner Top
                  </text>
                  <text x={labelX} y={hangerY + 8} fontSize="9" fill="#6B7280" textAnchor="end">
                    {(liner.top_ft || 0).toLocaleString()}'
                  </text>
                </g>
              )
            })}

            {/* Draw casing strings - no inline labels */}
            {visibility.casing && sortedCasing.map((casing, idx) => {
              const casingWidth = inchesToPixels(casing.size_in)
              const nextCasing = sortedCasing[idx + 1]
              const shoeY = depthToY(casing.bottom_ft)
              const leftX = wellCenterX - casingWidth / 2
              const rightX = wellCenterX + casingWidth / 2

              return (
                <g key={`casing-${idx}`}>
                  {/* Vertical lines */}
                  <line
                    x1={leftX}
                    y1={depthToY(casing.top_ft || 0)}
                    x2={leftX}
                    y2={shoeY}
                    stroke="#333"
                    strokeWidth="3"
                  />
                  <line
                    x1={rightX}
                    y1={depthToY(casing.top_ft || 0)}
                    x2={rightX}
                    y2={shoeY}
                    stroke="#333"
                    strokeWidth="3"
                  />

                  {/* Shoe - connect to next smaller casing */}
                  {nextCasing && (
                    <>
                      <line
                        x1={leftX}
                        y1={shoeY}
                        x2={wellCenterX - inchesToPixels(nextCasing.size_in) / 2}
                        y2={shoeY}
                        stroke="#333"
                        strokeWidth="3"
                      />
                      <line
                        x1={wellCenterX + inchesToPixels(nextCasing.size_in) / 2}
                        y1={shoeY}
                        x2={rightX}
                        y2={shoeY}
                        stroke="#333"
                        strokeWidth="3"
                      />
                    </>
                  )}

                  {/* Bottom shoe if last casing */}
                  {!nextCasing && (
                    <line
                      x1={leftX}
                      y1={shoeY}
                      x2={rightX}
                      y2={shoeY}
                      stroke="#333"
                      strokeWidth="3"
                    />
                  )}

                  {/* Casing shoe — right triangles between outermost casing OD and open hole */}
                  {(() => {
                    const outerCasingForShoe = idx > 0 ? sortedCasing[idx - 1] : null
                    if (outerCasingForShoe) return null  // inner casings: no shoe against open hole
                    if (!casing.hole_size_in) return null
                    const outerR = inchesToPixels(casing.hole_size_in) / 2
                    if (outerR <= casingWidth / 2) return null
                    const innerR = casingWidth / 2
                    const shoeH = 10
                    return (
                      <>
                        <polygon
                          points={`${wellCenterX - innerR},${shoeY - shoeH} ${wellCenterX - innerR},${shoeY} ${wellCenterX - outerR},${shoeY}`}
                          fill="#333"
                          stroke="#333"
                          strokeWidth="1"
                        />
                        <polygon
                          points={`${wellCenterX + innerR},${shoeY - shoeH} ${wellCenterX + innerR},${shoeY} ${wellCenterX + outerR},${shoeY}`}
                          fill="#333"
                          stroke="#333"
                          strokeWidth="1"
                        />
                      </>
                    )
                  })()}
                </g>
              )
            })}

            {adjustedLabels.map((label, idx) => {
              const outerCasingWidth = inchesToPixels(sortedCasing[0]?.hole_size_in || 10)
              const connectorStartX = wellCenterX + outerCasingWidth / 2 + 10
              const isForkedLabel = label.isForked === true

              return (
                <g key={`label-${idx}`}>
                  {isForkedLabel && label.topY !== undefined && label.bottomY !== undefined ? (
                    /* Forked connector for perforations - Y-shaped to point to top and bottom */
                    <>
                      {/* Main horizontal line from label to fork point */}
                      <line
                        x1={labelStartX - 5}
                        y1={label.adjustedY}
                        x2={connectorStartX + 20}
                        y2={label.adjustedY}
                        stroke="#9CA3AF"
                        strokeWidth="1"
                      />
                      {/* Fork to top perforation line */}
                      <line
                        x1={connectorStartX + 20}
                        y1={label.adjustedY}
                        x2={connectorStartX}
                        y2={label.topY}
                        stroke="#DC2626"
                        strokeWidth="1.5"
                      />
                      {/* Fork to bottom perforation line */}
                      <line
                        x1={connectorStartX + 20}
                        y1={label.adjustedY}
                        x2={connectorStartX}
                        y2={label.bottomY}
                        stroke="#DC2626"
                        strokeWidth="1.5"
                      />
                      {/* Dots at perforation ends */}
                      <circle cx={connectorStartX} cy={label.topY} r={2} fill="#DC2626" />
                      <circle cx={connectorStartX} cy={label.bottomY} r={2} fill="#DC2626" />
                    </>
                  ) : (
                    /* Standard connector for non-perforation labels */
                    <>
                      <line
                        x1={connectorStartX}
                        y1={label.y}
                        x2={labelStartX - 5}
                        y2={label.adjustedY}
                        stroke="#9CA3AF"
                        strokeWidth="1"
                      />
                      {/* Small dot at the wellbore end */}
                      <circle cx={connectorStartX} cy={label.y} r={2} fill={label.color} />
                    </>
                  )}
                  {/* Label text */}
                  <text x={labelStartX} y={label.adjustedY} fontSize="11" fill={label.color} fontWeight="600">
                    {label.text}
                  </text>
                  {label.subtext && (
                    <text x={labelStartX} y={label.adjustedY + 14} fontSize="10" fill={label.color}>
                      {label.subtext}
                    </text>
                  )}
                </g>
              )
            })}

            {/* Open hole section below deepest casing shoe */}
            {(() => {
              if (sortedCasing.length === 0) return null
              const deepestShoe = Math.max(...sortedCasing.map(c => c.bottom_ft || 0))
              if (maxDepth <= deepestShoe + 50) return null
              const shoeY = depthToY(deepestShoe)
              const tdY = depthToY(maxDepth)
              return (
                <g>
                  <line
                    x1={wellCenterX - 8}
                    y1={shoeY}
                    x2={wellCenterX - 8}
                    y2={tdY}
                    stroke="#9CA3AF"
                    strokeWidth="1.5"
                    strokeDasharray="6,4"
                  />
                  <line
                    x1={wellCenterX + 8}
                    y1={shoeY}
                    x2={wellCenterX + 8}
                    y2={tdY}
                    stroke="#9CA3AF"
                    strokeWidth="1.5"
                    strokeDasharray="6,4"
                  />
                </g>
              )
            })()}

            {/* Total depth marker */}
            <line
              x1={wellCenterX - 60}
              y1={depthToY(maxDepth)}
              x2={wellCenterX + 60}
              y2={depthToY(maxDepth)}
              stroke="#e53e3e"
              strokeWidth="2"
            />
            {/* TD label on far left to match depth markers */}
            <text
              x={5}
              y={depthToY(maxDepth) + 5}
              fontSize="11"
              fill="#e53e3e"
              fontWeight="bold"
              fontFamily="monospace"
            >
              TD {maxDepth.toLocaleString()}'
            </text>

            {/* Draw formation zones from well_geometry.formation_tops */}
            {visibility.formationTops && data.well_geometry?.formation_tops && data.well_geometry.formation_tops.length > 0 &&
              (() => {
                const depthMap = new Map<number, string[]>()

                // Only show formation tops within the well's total depth
                data.well_geometry.formation_tops
                  .filter(f => f.top_ft != null && f.top_ft <= maxDepth)
                  .forEach((formation) => {
                  if (formation.top_ft != null) {
                    if (!depthMap.has(formation.top_ft)) {
                      depthMap.set(formation.top_ft, [])
                    }
                    depthMap.get(formation.top_ft)!.push(formation.formation)
                  }
                })

                const sortedFormations = Array.from(depthMap.entries()).sort((a, b) => a[0] - b[0])

                // Compute adjusted label positions to avoid overlap
                const minFormationGap = 25 // Minimum pixels between formation labels
                const formationLabelPositions: Array<{ depth: number; names: string[]; trueY: number; adjustedY: number }> = []

                sortedFormations.forEach(([depth, formationNames], idx) => {
                  const trueY = depthToY(depth)
                  if (idx === 0) {
                    formationLabelPositions.push({ depth, names: formationNames, trueY, adjustedY: trueY })
                  } else {
                    const prevAdjustedY = formationLabelPositions[idx - 1].adjustedY
                    const adjustedY = trueY - prevAdjustedY < minFormationGap ? prevAdjustedY + minFormationGap : trueY
                    formationLabelPositions.push({ depth, names: formationNames, trueY, adjustedY })
                  }
                })

                const labelX = wellCenterX - inchesToPixels(sortedCasing[0]?.hole_size_in || 10) / 2 - 5
                const lineEndX = wellCenterX + inchesToPixels(sortedCasing[0]?.hole_size_in || 10) / 2 + 5

                return formationLabelPositions.map((item, idx) => {
                  const uniqueFormations = Array.from(new Set(item.names))
                  const combinedLabel = uniqueFormations.join("/")
                  const formationColors = getFormationColors(uniqueFormations[0])
                  const isOffset = Math.abs(item.adjustedY - item.trueY) > 2

                  return (
                    <g key={`formation-zone-${idx}`}>
                      {/* Formation zone dashed line at TRUE depth */}
                      <line
                        x1={0}
                        y1={item.trueY}
                        x2={lineEndX}
                        y2={item.trueY}
                        stroke={formationColors.border}
                        strokeWidth="2"
                        strokeDasharray="5,5"
                      />

                      {/* Connector from true depth to adjusted label position */}
                      {isOffset && (
                        <line
                          x1={labelX + 5}
                          y1={item.trueY}
                          x2={labelX + 5}
                          y2={item.adjustedY - 7}
                          stroke={formationColors.border}
                          strokeWidth="1"
                          opacity="0.5"
                        />
                      )}

                      {/* Formation name at ADJUSTED position */}
                      <text
                        x={labelX}
                        y={item.adjustedY - 5}
                        fontSize="11"
                        fill={formationColors.text}
                        fontWeight="600"
                        textAnchor="end"
                      >
                        {combinedLabel}
                      </text>

                      {/* Depth value at ADJUSTED position */}
                      <text
                        x={labelX}
                        y={item.adjustedY + 10}
                        fontSize="10"
                        fill={formationColors.text}
                        textAnchor="end"
                      >
                        {item.depth.toLocaleString()}'
                      </text>
                    </g>
                  )
                })
              })()}

            {/* KOP (Kickoff Point) marker */}
            {data.well_geometry.kop_ft != null && (() => {
              const kopY = depthToY(data.well_geometry.kop_ft!)
              const outerHoleSize = sortedCasing[0]?.hole_size_in || sortedCasing[0]?.size_in || 10
              const lineEndX = wellCenterX + inchesToPixels(outerHoleSize) / 2 + 5
              const labelX = wellCenterX - inchesToPixels(outerHoleSize) / 2 - 5
              return (
                <g key="kop-marker">
                  <line x1={0} y1={kopY} x2={lineEndX} y2={kopY} stroke="#7C3AED" strokeWidth="2" strokeDasharray="8,4" />
                  <text x={labelX} y={kopY - 5} fontSize="11" fill="#7C3AED" fontWeight="700" textAnchor="end">KOP</text>
                  <text x={labelX} y={kopY + 10} fontSize="10" fill="#7C3AED" textAnchor="end">
                    {data.well_geometry.kop_ft!.toLocaleString()}'
                  </text>
                </g>
              )
            })()}

            {/* Draw production perforations - thin red lines crossing the casing horizontally - DRAWN LAST SO ON TOP */}
            {visibility.perforations && data.well_geometry.production_perforations?.map((perf, idx) => {
              const topY = depthToY(perf.top_ft)
              const bottomY = depthToY(perf.bottom_ft)

              // Find the innermost casing at this depth
              let casingWidth = inchesToPixels(sortedCasing[sortedCasing.length - 1]?.size_in || 7)
              for (let i = sortedCasing.length - 1; i >= 0; i--) {
                if (perf.top_ft <= sortedCasing[i].bottom_ft) {
                  casingWidth = inchesToPixels(sortedCasing[i].size_in)
                  break // Stop at first (innermost) casing that contains this depth
                }
              }
              
              // Extend lines well beyond casing for high visibility
              const extension = 15

              return (
                <g key={`prod-perf-${idx}`}>
                  {/* Top perforation line - thin red line crossing horizontally */}
                  <line
                    x1={wellCenterX - casingWidth / 2 - extension}
                    y1={topY}
                    x2={wellCenterX + casingWidth / 2 + extension}
                    y2={topY}
                    stroke="#DC2626"
                    strokeWidth="2"
                  />
                  {/* Bottom perforation line - thin red line crossing horizontally */}
                  <line
                    x1={wellCenterX - casingWidth / 2 - extension}
                    y1={bottomY}
                    x2={wellCenterX + casingWidth / 2 + extension}
                    y2={bottomY}
                    stroke="#DC2626"
                    strokeWidth="2"
                  />
                  {/* Add small vertical markers at casing edges to show perforation interval */}
                  <line
                    x1={wellCenterX - casingWidth / 2}
                    y1={topY}
                    x2={wellCenterX - casingWidth / 2}
                    y2={bottomY}
                    stroke="#DC2626"
                    strokeWidth="1"
                    strokeDasharray="3,2"
                    opacity="0.5"
                  />
                  <line
                    x1={wellCenterX + casingWidth / 2}
                    y1={topY}
                    x2={wellCenterX + casingWidth / 2}
                    y2={bottomY}
                    stroke="#DC2626"
                    strokeWidth="1"
                    strokeDasharray="3,2"
                    opacity="0.5"
                  />
                </g>
              )
            })}

            {/* Draw NEW perforations for perf & squeeze AND perf & circulate plugs - orange triangles at deeper end - DRAWN LAST SO ON TOP */}
            {visibility.perforations && data.payload?.steps
              ?.filter((step) => 
                (step.plug_type === "perf_and_squeeze_plug" || step.plug_type === "perf_and_circulate_plug") 
                && step.top_ft != null && step.bottom_ft != null
              )
              .map((step, stepIdx) => {
                // Perforations are at the DEEPER end (larger depth number) of the interval
                const perfDepth = Math.max(step.top_ft!, step.bottom_ft!)
                const perfY = depthToY(perfDepth)

                // Find the innermost (production) casing at this depth
                // sortedCasing[length-1] is the innermost (smallest), [0] is outermost (largest)
                let productionCasingWidth = inchesToPixels(sortedCasing[sortedCasing.length - 1]?.size_in || 7)
                
                // Start from innermost and work outward, stop at first casing that contains this depth
                for (let i = sortedCasing.length - 1; i >= 0; i--) {
                  if (perfDepth <= sortedCasing[i].bottom_ft) {
                    productionCasingWidth = inchesToPixels(sortedCasing[i].size_in)
                    break // Found the innermost casing at this depth
                  }
                }

                // Triangle dimensions - 1/2 thickness, 2/3 length
                const triangleBase = 5  // Half of original (10)
                const triangleHeight = 20  // 2/3 of original (30)
                
                // Start inside production casing (2 pixels inside the wall)
                const startRadius = (productionCasingWidth / 2) - 2

                return (
                  <g key={`new-perf-${step.step_id ?? stepIdx}`}>
                    {/* Left side - triangle pointing left, starting inside production casing */}
                    <polygon
                      points={`
                        ${wellCenterX - startRadius},${perfY - triangleBase / 2}
                        ${wellCenterX - startRadius},${perfY + triangleBase / 2}
                        ${wellCenterX - startRadius - triangleHeight},${perfY}
                      `}
                      fill="#F97316"
                      stroke="#D97706"
                      strokeWidth="1"
                      opacity="0.9"
                    />
                    {/* Right side - triangle pointing right, starting inside production casing */}
                    <polygon
                      points={`
                        ${wellCenterX + startRadius},${perfY - triangleBase / 2}
                        ${wellCenterX + startRadius},${perfY + triangleBase / 2}
                        ${wellCenterX + startRadius + triangleHeight},${perfY}
                      `}
                      fill="#F97316"
                      stroke="#D97706"
                      strokeWidth="1"
                      opacity="0.9"
                    />
                  </g>
                )
              })}
          </svg>
        </div>

        {/* Legend */}
        <div className="mt-6 p-4 bg-brand-50 rounded-lg">
          <h3 className="font-bold text-sm text-brand-900 mb-3">Legend</h3>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <svg width="24" height="16">
                <defs>
                  <pattern id="legendNewCement" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                    <rect width="6" height="6" fill="#22c55e" />
                    <circle cx="3" cy="3" r="0.5" fill="#000" />
                  </pattern>
                </defs>
                <rect width="24" height="16" fill="url(#legendNewCement)" stroke="#22C55E" strokeWidth="1" />
              </svg>
              <span>New Cement</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="24" height="16">
                <defs>
                  <pattern id="legendExistingCement" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                    <rect width="6" height="6" fill="#9CA3AF" />
                    <circle cx="3" cy="3" r="0.5" fill="#000" />
                  </pattern>
                </defs>
                <rect width="24" height="16" fill="url(#legendExistingCement)" stroke="#6B7280" strokeWidth="1" />
              </svg>
              <span>Existing Cement</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="24" height="16">
                <rect width="24" height="16" fill="#FCD34D" stroke="#000" strokeWidth="1" />
                <line x1="2" y1="2" x2="22" y2="14" stroke="#000" strokeWidth="2" />
                <line x1="22" y1="2" x2="2" y2="14" stroke="#000" strokeWidth="2" />
              </svg>
              <span>CIBP</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="30" height="6">
                <line x1="0" y1="3" x2="30" y2="3" stroke="#DC2626" strokeWidth="3" />
              </svg>
              <span>Production Perfs</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="30" height="12">
                {/* Show triangular perforation marker */}
                <polygon
                  points="8,3 8,9 2,6"
                  fill="#F97316"
                  stroke="#D97706"
                  strokeWidth="0.5"
                />
                <polygon
                  points="22,3 22,9 28,6"
                  fill="#F97316"
                  stroke="#D97706"
                  strokeWidth="0.5"
                />
              </svg>
              <span>New Perfs (P&S)</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="30" height="2">
                <line x1="0" y1="1" x2="30" y2="1" stroke="#333" strokeWidth="3" />
              </svg>
              <span>Casing</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="30" height="2">
                <line x1="0" y1="1" x2="30" y2="1" stroke="#F59E0B" strokeWidth="2" strokeDasharray="3,3" />
              </svg>
              <span>Tubing</span>
            </div>
          </div>
        </div>

        {/* Casing Information Table */}
        <div className="mt-6">
          <h3 className="font-bold text-sm text-brand-900 mb-3">Casing Information</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Item Type</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Size (in)</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Hole Size (in)</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Top (ft)</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Bottom (ft)</th>
                </tr>
              </thead>
              <tbody>
                {/* Casing strings */}
                {sortedCasing.map((casing, idx) => (
                  <tr key={`casing-table-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-3 py-2 text-gray-900 font-medium capitalize">{casing.string || 'Unknown'} Casing</td>
                    <td className="px-3 py-2 text-gray-700">{casing.size_in != null ? parseFloat(Number(casing.size_in).toFixed(3)) : "—"}"</td>
                    <td className="px-3 py-2 text-gray-700">{casing.hole_size_in != null ? parseFloat(Number(casing.hole_size_in).toFixed(3)) : "—"}"</td>
                    <td className="px-3 py-2 text-gray-700">{(casing.top_ft || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-gray-700">{(casing.bottom_ft ?? 0).toLocaleString()}</td>
                  </tr>
                ))}
                
                {/* Tubing rows */}
                {data.well_geometry?.tubing && data.well_geometry.tubing.length > 0 &&
                  data.well_geometry.tubing.map((tubing, idx) => (
                    <tr key={`tubing-table-${idx}`} className={sortedCasing.length % 2 === idx % 2 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-3 py-2 text-gray-900 font-medium">Tubing</td>
                      <td className="px-3 py-2 text-gray-700">{tubing.size_in != null ? parseFloat(Number(tubing.size_in).toFixed(3)) : "—"}"</td>
                      <td className="px-3 py-2 text-gray-700">—</td>
                      <td className="px-3 py-2 text-gray-700">{(tubing.top_ft || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-gray-700">{(tubing.bottom_ft ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                
                {/* Liner rows */}
                {data.well_geometry?.liner && data.well_geometry.liner.length > 0 &&
                  data.well_geometry.liner.map((liner, idx) => (
                    <tr key={`liner-table-${idx}`} className={(sortedCasing.length + (data.well_geometry.tubing?.length || 0)) % 2 === idx % 2 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-3 py-2 text-gray-900 font-medium">Liner</td>
                      <td className="px-3 py-2 text-gray-700">{liner.size_in != null ? parseFloat(Number(liner.size_in).toFixed(3)) : "—"}"</td>
                      <td className="px-3 py-2 text-gray-700">—</td>
                      <td className="px-3 py-2 text-gray-700">{(liner.top_ft || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-gray-700">{liner.bottom_ft ? liner.bottom_ft.toLocaleString() : "—"}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Plugging Steps Table */}
        <div className="mt-6">
          <h3 className="font-bold text-sm text-brand-900 mb-3">Plugging Steps</h3>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-brand-900 text-white">
                <th className="p-2 text-left">Step</th>
                <th className="p-2 text-left">Type</th>
                <th className="p-2 text-left">Top (ft)</th>
                <th className="p-2 text-left">Bottom (ft)</th>
                <th className="p-2 text-left">Sacks</th>
                <th className="p-2 text-left">Regulatory Basis</th>
                <th className="p-2 text-left">Plug Purpose</th>
              </tr>
            </thead>
            <tbody>
              {data.payload.steps.map((step, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                  <td className="p-2 font-semibold">{step.step_id ?? idx + 1}</td>
                  <td className="p-2">{step.display_name || step.type}</td>
                  <td className="p-2">{step.top_ft != null ? step.top_ft.toLocaleString() : "-"}</td>
                  <td className="p-2">{step.bottom_ft != null ? step.bottom_ft.toLocaleString() : "-"}</td>
                  <td className="p-2">{step.sacks || "-"}</td>
                  <td className="p-2">{Array.isArray(step.regulatory_basis) ? step.regulatory_basis.join(", ") : (step.regulatory_basis || "-")}</td>
                  <td className="p-2">{step.plug_purpose || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
})

export default ProposedPlugDiagram

// Input types that match the API response (more flexible than internal diagram types)
interface ApiCasingString {
  string?: string
  casing_type?: string
  string_type?: string
  size_in: number | string
  top_ft?: number | null
  bottom_ft: number
  shoe_depth_ft?: number
  hole_size_in?: number | string
  cement_top_ft?: number | null
}

interface ApiFormationTop {
  formation: string
  top_ft: number
  base_ft?: number | null
}

interface ApiTubing {
  size_in: number
  bottom_ft: number
  top_ft?: number | null
  shoe_depth_ft?: number
}

interface PlanInput {
  well?: {
    api14?: string
    api_number?: string
    operator_name?: string
    operator?: string
    field_name?: string
    well_number?: string
    lease_name?: string
  }
  well_geometry?: {
    casing_strings?: ApiCasingString[]
    formation_tops?: ApiFormationTop[]
    production_perforations?: Perforation[]
    perforations?: Perforation[]
    tubing?: ApiTubing[]
    existing_tools?: ExistingTool[]
    liner?: Liner[] | unknown[]
  }
  payload?: {
    steps?: Step[]
    formations_targeted?: string[]
    materials_totals?: {
      total_sacks?: number
      total_bbl?: number
    }
  }
  steps?: Step[]
}

/** Parse a fraction string like "9 5/8" to 9.625, or return the number if already numeric. */
function parseFraction(val: unknown): number {
  if (val == null || val === "") return 0;
  if (typeof val === "number") return val;
  const s = String(val).trim();
  const num = Number(s);
  if (!isNaN(num)) return num;
  // Try "whole num/den" like "9 5/8"
  const parts = s.split(/\s+/);
  if (parts.length === 2 && parts[1].includes("/")) {
    const whole = Number(parts[0]);
    const [n, d] = parts[1].split("/").map(Number);
    if (!isNaN(whole) && !isNaN(n) && d) return whole + n / d;
  }
  // Try pure fraction "5/8"
  if (s.includes("/")) {
    const [n, d] = s.split("/").map(Number);
    if (!isNaN(n) && d) return n / d;
  }
  return 0;
}

export function transformPlanToProposedDiagramData(plan: PlanInput): ProposedPlugDiagramData {
  // Transform API casing strings to include hole_size_in
  const casingStrings: CasingString[] = (plan.well_geometry?.casing_strings || []).map((casing) => {
    // If hole_size_in is provided, use it; otherwise estimate from next larger casing or use a default
    let holeSizeIn = casing.hole_size_in ? parseFraction(casing.hole_size_in) : undefined
    const casingLabel = (casing.string || casing.casing_type || casing.string_type || '').toLowerCase()

    if (!holeSizeIn) {
      // Estimate hole size: typically 2-4" larger than casing OD
      // For surface casing, use a larger default
      const casingSize = parseFraction(casing.size_in)
      if (casingLabel.includes('surface')) {
        holeSizeIn = casingSize + 3.5  // Surface casing typically drilled 3-4" larger
      } else if (casingLabel.includes('production')) {
        holeSizeIn = casingSize + 2  // Production casing typically drilled 2-3" larger
      } else {
        holeSizeIn = casingSize + 2.5  // Default estimate
      }
    }

    return {
      string: casing.string || casing.casing_type || casing.string_type || '',
      size_in: parseFraction(casing.size_in),
      top_ft: casing.top_ft ?? 0,
      bottom_ft: casing.bottom_ft ?? casing.shoe_depth_ft ?? 0,
      hole_size_in: holeSizeIn,
      cement_top_ft: casing.cement_top_ft,
    }
  })

  // Transform formation tops
  const formationTops: FormationTop[] = (plan.well_geometry?.formation_tops || []).map(ft => ({
    formation: ft.formation,
    top_ft: ft.top_ft,
    base_ft: ft.base_ft ?? null,
  }))

  // Transform tubing
  const tubing: Tubing[] = (plan.well_geometry?.tubing || []).map(t => ({
    size_in: t.size_in,
    bottom_ft: t.bottom_ft ?? t.shoe_depth_ft ?? 0,
    top_ft: t.top_ft ?? null,
  }))

  // Handle perforations (might be in production_perforations or perforations)
  const perforations = plan.well_geometry?.production_perforations || plan.well_geometry?.perforations || []

  const transformedData: ProposedPlugDiagramData = {
    well: {
      api14: plan.well?.api14 || plan.well?.api_number || "",
      operator_name: plan.well?.operator_name || plan.well?.operator || "",
      field_name: plan.well?.field_name || "",
      well_number: plan.well?.well_number || "",
      lease_name: plan.well?.lease_name || "",
    },
    well_geometry: {
      casing_strings: casingStrings,
      formation_tops: formationTops,
      production_perforations: perforations,
      tubing: tubing,
      existing_tools: plan.well_geometry?.existing_tools || [],
      liner: (plan.well_geometry?.liner || []) as Liner[],
    },
    payload: {
      steps: plan.payload?.steps || plan.steps || [],
      formations_targeted: plan.payload?.formations_targeted || [],
      materials_totals: plan.payload?.materials_totals || {},
    },
  }
  
  return transformedData
}
